import React, { useState, useEffect, useRef } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { useProject } from '../../contexts/ProjectContext';
import {
  useOrchestratorProjectGuard,
  OrchestratorProjectGuard,
} from '../../hooks/useOrchestratorProjectGuard';
import { clearOrchestratorContext, useOrchestratorContext } from '../../hooks/useOrchestratorContext';

const BANNER_DELAY_MS = 5000; // wait for project switch transition to complete before warning

interface Props {
  // Pass the guard result when the host page already calls the hook itself,
  // so the guard effect doesn't run twice. Omit it on pages that don't.
  guard?: OrchestratorProjectGuard;
}

const OrchestratorProjectMismatchBanner: React.FC<Props> = ({ guard: guardProp }) => {
  const ownGuard = useOrchestratorProjectGuard(!guardProp);
  const guard = guardProp || ownGuard;
  const { projects, setCurrentProject } = useProject();
  const [dismissed, setDismissed] = useState(false);
  const [visible, setVisible] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const ownCtx = useOrchestratorContext();

  useEffect(() => {
    // Only show banner when user arrived via Smart Router URL params (active step).
    // If they navigated directly via sidebar (fromUrl=false), they're intentionally
    // working on a different project — don't confuse them with a stale session warning.
    if (guard.mismatch && !dismissed && ownCtx.fromUrl) {
      timerRef.current = setTimeout(() => setVisible(true), BANNER_DELAY_MS);
    } else {
      setVisible(false);
      if (timerRef.current) clearTimeout(timerRef.current);
    }
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [guard.mismatch, dismissed, ownCtx.fromUrl]);

  if (!visible || !guard.mismatch || dismissed) return null;

  const expected = projects.find((p) => p.id === guard.expectedProjectId);

  return (
    <div className="mx-2 mt-2 bg-amber-50 border border-amber-200 rounded-xl">
      <div className="px-4 py-2.5 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <span className="text-sm text-amber-800 min-w-0">
            You&apos;ve switched away from this Smart Router session&apos;s project
            {expected ? ` (${expected.name})` : guard.expectedProjectId ? ` (#${guard.expectedProjectId})` : ''}.
            Work on this step is still tied to that project.
          </span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {expected && (
            <button
              onClick={() => setCurrentProject(expected)}
              className="text-xs font-semibold bg-amber-600 text-white hover:bg-amber-700 px-3 py-1.5 rounded-lg"
            >
              Switch back
            </button>
          )}
          <button
            onClick={() => { clearOrchestratorContext(); setDismissed(true); }}
            className="p-1 hover:bg-amber-100 rounded text-amber-600"
            title="Dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrchestratorProjectMismatchBanner;
